import {IRootStore} from "../rootStore";
import {IModalStore, ModalStore} from "./global/modalStore";
import {AccountStore, IAccountStore} from "./global/accountStore";
import {EventsUiStore, IEventsUiStore} from "./pages/eventsUiStore";
import {INoticesUiStore, NoticesUiStore} from "./pages/noticesUiStore";
import {
  IToastMessageStore,
  ToastMessageStore
} from "./global/toastMessageStore";
import {reaction, runInAction} from "mobx";
import {KeyValue} from "../observables/keyValue";
import {
  IResidentAdminUiStore,
  ResidentAdminUiStore
} from "./pages/residentAdminUiStore";
import {RoutePaths} from "../../shared/routePaths";
import {IUserService} from "../../services/userService";
import {
  EventCatalogueUiStore,
  IEventCatalogueUiStore
} from "./pages/eventCatalogueUiStore";
import {ObservableList} from "../observables/observableList";
import {
  multiVillageKeyValue,
  ServicedApartmentResidentsFeatureName,
  villageFeatureKey
} from "../../shared/constants";
import {isFeatureToggleEnabled} from "../../utils/featureToggle/featureToggle";
import {MultiVillageHelper} from "../../shared/multiVillageHelper";
import {
  ITransactionsUiStore,
  TransactionsUiStore
} from "./pages/transactionsUiStore";
import {IOccasionUiStore, OccasionUiStore} from "./pages/occasionUiStore";
import {
  IVillageCalendarsUiStore,
  VillageCalendarsUiStore
} from "./pages/villageCalendarsUiStore";

export interface IUiStore {
  rootStore: IRootStore;
  accountStore: IAccountStore;
  modalStore: IModalStore;
  toastMessageStore: IToastMessageStore;
  eventsUiStore: IEventsUiStore;
  noticesUiStore: INoticesUiStore;
  transactionsUiStore: ITransactionsUiStore;
  selectableVillages: ObservableList<KeyValue>;
  independentResidentAdminUiStore: IResidentAdminUiStore;
  servicedApartmentResidentAdminUiStore: IResidentAdminUiStore;
  eventCatalogueUiStore: IEventCatalogueUiStore;
  occasionUiStore: IOccasionUiStore;
  villageCalendarsUiStore: IVillageCalendarsUiStore;
  /**
   * The path name of the current URL.
   */
  currentPathName: string;
  /**
   * Reset all ui store state
   */
  resetAllState: () => void;
  onSelectedVillageChange: () => void;
  isMultiVillageOptionSelected: () => boolean;
}

export class UiStore implements IUiStore {
  private readonly userService: IUserService;
  private selectedVillageId?: string;
  rootStore: IRootStore;
  accountStore: IAccountStore;
  modalStore: IModalStore;
  toastMessageStore: IToastMessageStore;
  eventsUiStore: IEventsUiStore;
  noticesUiStore: INoticesUiStore;
  transactionsUiStore: ITransactionsUiStore;
  independentResidentAdminUiStore: IResidentAdminUiStore;
  servicedApartmentResidentAdminUiStore: IResidentAdminUiStore;
  selectableVillages: ObservableList<KeyValue>;
  currentPathName: string = "";
  eventCatalogueUiStore: IEventCatalogueUiStore;
  occasionUiStore: IOccasionUiStore;
  villageCalendarsUiStore: IVillageCalendarsUiStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    this.accountStore = new AccountStore(
      rootStore.transportServices.accountService
    );
    this.selectableVillages = new ObservableList<KeyValue>([]);
    this.modalStore = new ModalStore();
    this.toastMessageStore = new ToastMessageStore();
    this.eventsUiStore = new EventsUiStore(
      rootStore.domainStore.eventsDomainStore,
      this.modalStore,
      this.selectableVillages,
      rootStore.domainStore.eventBookingResidentsDomainStore,
      rootStore.domainStore.villageCalendarsDomainStore
    );
    this.noticesUiStore = new NoticesUiStore(
      rootStore.domainStore.noticesDomainStore,
      this.modalStore,
      this.selectableVillages
    );
    this.transactionsUiStore = new TransactionsUiStore(
      rootStore.domainStore.transactionsDomainStore,
      this.modalStore
    );
    this.independentResidentAdminUiStore = new ResidentAdminUiStore(
      rootStore.domainStore.independentResidentAdminDomainStore,
      this.modalStore,
      this.selectableVillages,
      this.accountStore
    );
    this.servicedApartmentResidentAdminUiStore = new ResidentAdminUiStore(
      rootStore.domainStore.servicedApartmentResidentAdminDomainStore,
      this.modalStore,
      this.selectableVillages,
      this.accountStore
    );
    this.eventCatalogueUiStore = new EventCatalogueUiStore(
      rootStore.domainStore.eventsDomainStore,
      this.eventsUiStore,
      rootStore.domainStore.eventCatalogueDomainStore,
      this.modalStore,
      rootStore.domainStore.villageCalendarsDomainStore
    );
    this.occasionUiStore = new OccasionUiStore();
    this.villageCalendarsUiStore = new VillageCalendarsUiStore(
      rootStore.domainStore.personTypesDomainStore
    );
    this.userService = this.rootStore.userService;

    reaction(
      () => this.accountStore.villages,
      () => {
        if (this.accountStore.villages?.length) {
          const villages = this.accountStore.villages.map(
            (village) => new KeyValue(village.id, village.name)
          );

          if (this.accountStore.abilities.can("Create", "MultiVillage"))
            villages.unshift(multiVillageKeyValue);

          this.selectableVillages.setItems(villages);

          if (villages.length === 1) {
            this.selectableVillages.setSelectedItem(
              this.accountStore.villages![0].id
            );
          }
        } else {
          this.selectableVillages.setItems([]);
          this.selectableVillages.setSelectedItem(null);
        }
        this.onSelectedVillageChange();
      }
    );

    reaction(
      () => this.selectableVillages.selectedItem,
      () => {
        runInAction(() => {
          this.onSelectedVillageChange();
        });
      }
    );
  }

  resetAllState = () => {
    runInAction(() => {
      this.eventsUiStore.isCreatingOrEditing = false;
      this.eventsUiStore.isViewingEvent = false;
      this.noticesUiStore.isCreatingOrEditing = false;
      this.independentResidentAdminUiStore.isCreatingOrEditing = false;
      this.servicedApartmentResidentAdminUiStore.isCreatingOrEditing = false;
      this.eventCatalogueUiStore.isSearch = false;
      this.noticesUiStore.isViewingNotice = false;
    });
  };

  isMultiVillageOptionSelected(): boolean {
    return (
      this.selectableVillages.selectedItem?.value === multiVillageKeyValue.value
    );
  }

  onSelectedVillageChange = () => {
    const selectedVillage = this.accountStore.villages?.find(
      (v) => v.id === this.selectableVillages.selectedItem?.value
    );
    const newSelectedVillageId = selectedVillage
      ? selectedVillage.id
      : this.isMultiVillageOptionSelected()
        ? multiVillageKeyValue.value
        : undefined;
    const hasSelectedVillageChanged =
      newSelectedVillageId !== this.selectedVillageId;

    if (selectedVillage) {
      this.userService.setSelectedVillage(selectedVillage);
    } else if (this.isMultiVillageOptionSelected()) {
      this.userService.setSelectedVillage(
        MultiVillageHelper.multiVillageVillage
      );
    }

    if (hasSelectedVillageChanged) {
      this.independentResidentAdminUiStore.resetResidentData();
      this.servicedApartmentResidentAdminUiStore.resetResidentData();
      this.selectedVillageId = newSelectedVillageId;
    }

    switch (this.currentPathName) {
      case RoutePaths.Events:
        runInAction(() => {
          this.eventsUiStore.onSelectedVillageChange();
        });
        break;

      case RoutePaths.Notices:
        runInAction(() => {
          this.noticesUiStore.onSelectedVillageChange();
        });
        break;

      case RoutePaths.IndependentResidentAdmin:
        runInAction(() => {
          this.independentResidentAdminUiStore.onSelectedVillageChange();
        });
        break;

      case RoutePaths.ServicedApartmentResidentAdmin:
        // The nav menu redirects away from this page for villages without SA residents
        if (
          isFeatureToggleEnabled(ServicedApartmentResidentsFeatureName) ||
          isFeatureToggleEnabled(
            `${villageFeatureKey}_${ServicedApartmentResidentsFeatureName}_${newSelectedVillageId}`
          )
        ) {
          runInAction(() => {
            this.servicedApartmentResidentAdminUiStore.onSelectedVillageChange();
          });
        }
        break;

      case RoutePaths.Transactions:
        runInAction(() => {
          if (
            this.selectableVillages.selectedItem?.value !==
            multiVillageKeyValue.value
          ) {
            this.transactionsUiStore.onSelectedVillageChange();
          }
        });
    }
  };
}
